import { useEffect, useState } from 'react'
import { Box, TextField } from '@mui/material'
import { styled } from '@mui/material/styles'
import {
  Section,
  SectionTitle,
  SectionSubtitle,
  ActionsRow,
} from '../../Profile/profile-styles'
import {
  MovieceptionCancelButton,
  MovieceptionPrimaryButton,
} from '../../../components/Buttons/movieception-buttons'
import { useAuth } from '../../../context/AuthContext'
import { useSnackbar } from '../../../context/snackbar-context'
import { usersApi } from '../../../client/API'

const FieldsColumn = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2.5),
}))

const USERNAME_PATTERN = /^[a-z0-9_]{3,30}$/

export const ProfileSection = () => {
  const { user, getAccessToken, refreshUser } = useAuth()
  const { enqueueSnackbar } = useSnackbar()
  const [displayName, setDisplayName] = useState(user?.name || '')
  const [username, setUsername] = useState(user?.username || '')
  const [bio, setBio] = useState(user?.bio || '')
  const [saving, setSaving] = useState(false)
  const [usernameTaken, setUsernameTaken] = useState(false)

  // The user often arrives after first render (fetched on auth); sync when it does.
  useEffect(() => {
    setDisplayName(user?.name || '')
    setUsername(user?.username || '')
    setBio(user?.bio || '')
  }, [user?.name, user?.username, user?.bio])

  const trimmedName = displayName.trim()
  const trimmedUsername = username.trim().toLowerCase()
  const trimmedBio = bio.trim()

  const nameDirty = trimmedName !== (user?.name || '')
  const usernameDirty = trimmedUsername !== (user?.username || '')
  const bioDirty = trimmedBio !== (user?.bio || '')
  const isDirty = nameDirty || usernameDirty || bioDirty

  const usernameInvalid =
    usernameDirty && !USERNAME_PATTERN.test(trimmedUsername)
  const usernameAllDigits = usernameDirty && /^[0-9_]+$/.test(trimmedUsername)

  // Live availability check, debounced while typing.
  useEffect(() => {
    setUsernameTaken(false)
    if (!usernameDirty || usernameInvalid || usernameAllDigits) return

    let cancelled = false
    const timer = setTimeout(async () => {
      try {
        const token = await getAccessToken()
        const response = await usersApi.usernameAvailable(
          token,
          trimmedUsername,
        )
        if (!cancelled) setUsernameTaken(!response.data.available)
      } catch {
        /* check silently fails - the save still validates server-side */
      }
    }, 400)

    return () => {
      cancelled = true
      clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimmedUsername, usernameDirty, usernameInvalid, usernameAllDigits])

  const usernameError =
    usernameInvalid || usernameAllDigits
      ? 'Lowercase letters, numbers and underscores; 3-30 chars, at least one letter.'
      : usernameTaken
        ? 'That username is taken.'
        : undefined

  const handleSave = async () => {
    if (!isDirty || !trimmedName || usernameError) return
    setSaving(true)
    try {
      const token = await getAccessToken()
      await usersApi.updateProfile(token, {
        ...(nameDirty ? { name: trimmedName } : {}),
        ...(usernameDirty ? { username: trimmedUsername } : {}),
        ...(bioDirty ? { bio: trimmedBio } : {}),
      })
      await refreshUser()
      enqueueSnackbar('Profile updated', { variant: 'success' })
    } catch (error) {
      const message =
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message ?? 'Could not update your profile. Please try again.'
      enqueueSnackbar(message, { variant: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = () => {
    setDisplayName(user?.name || '')
    setUsername(user?.username || '')
    setBio(user?.bio || '')
  }

  return (
    <Section>
      <SectionTitle>Profile</SectionTitle>
      <SectionSubtitle>
        Manage how your profile appears across Movieception.
      </SectionSubtitle>

      <FieldsColumn>
        <TextField
          label='Display name'
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          size='small'
          disabled={saving}
          inputProps={{ maxLength: 255 }}
        />
        <TextField
          label='Username'
          value={username}
          onChange={(e) => setUsername(e.target.value.toLowerCase())}
          size='small'
          disabled={saving}
          error={!!usernameError}
          helperText={
            usernameError ??
            `Your public profile lives at movieception.com/user/${trimmedUsername || 'username'}`
          }
          inputProps={{ maxLength: 30 }}
        />
        <TextField
          label='Bio'
          value={bio}
          onChange={(e) => setBio(e.target.value)}
          size='small'
          disabled={saving}
          multiline
          minRows={2}
          helperText={`${trimmedBio.length}/500 - shown on your public profile`}
          inputProps={{ maxLength: 500 }}
        />
      </FieldsColumn>

      <ActionsRow>
        <MovieceptionCancelButton
          label='Cancel'
          onClick={handleCancel}
          disabled={!isDirty || saving}
        />
        <MovieceptionPrimaryButton
          label={saving ? 'Saving…' : 'Save Changes'}
          onClick={handleSave}
          disabled={!isDirty || !trimmedName || !!usernameError || saving}
        />
      </ActionsRow>
    </Section>
  )
}
